import { useAbility } from "@casl/react";
import {
  useClub,
  useClubFeatureFlags,
  useMutationXeroDisableIntegration,
  useMutationXeroEnableIntegration,
  useQueryXeroGetIntegrationStatus,
  useQueryXeroGetRedirectUrlToAuthPage,
} from "@gymflow/api";
import { KisiStatus } from "@gymflow/types";
import { isAxiosError } from "axios";
import { ReactNode, useContext } from "react";
import { useHistory } from "react-router";

import { usePortalRoutes } from "../../hooks/usePortalRoutes";
import { AbilityContext, Subject, Verb } from "../../permissions";
import { useClubSettings } from "../../providers";
import { ToastContext } from "../../providers/ToastProvider/context";
import { RouteFeature } from "../../routes/feature";
import { RouteLayout } from "../../routes/layout";
import useGymflowModels from "../../store";
import { AlertCircleIcon, Button, LinkButton, Spinner, Switch } from "../atoms";
import CustomTooltip from "../atoms/CustomTooltip";
import { Settings04Icon } from "../atoms/icons/Settings04Icon";
import { SettingsContainer } from "../Settings/SettingsContainer";
import { kisiIntegrationRoutes } from "./KisiIntegration";

export function Integrations() {
  const { clubId } = useClubSettings();
  const toast = useContext(ToastContext);
  const { api } = useGymflowModels();
  const history = useHistory();
  const { createClubLink, createAbsoluteClubLink } = usePortalRoutes();
  const ability = useAbility(AbilityContext);
  const { data: featureFlags } = useClubFeatureFlags({ clubId, api });

  const { data: club } = useClub({ clubId, api });

  const { data: dataXero, isLoading: isLoadingXero } =
    useQueryXeroGetIntegrationStatus({
      api,
      clubId,
      refetchOnWindowFocus: true,
    });

  const { mutateAsync: mutateAsyncEnableXero } =
    useMutationXeroEnableIntegration({ api });

  const { mutateAsync: mutateAsyncDisableXero } =
    useMutationXeroDisableIntegration({ api });

  const { refetch: refetchRedirectAuthUrlXero } =
    useQueryXeroGetRedirectUrlToAuthPage({
      api,
      opts: {
        appUrl: createAbsoluteClubLink(
          RouteLayout.Staff,
          `${RouteFeature.XeroIntegration}?authenticatedToast=show`,
        ),
      },
      enabled: false,
    });

  const isKisiProcessing = club?.kisiStatus
    ? (
        [
          "PROCESSING_ENABLE",
          "PROCESSING_DISABLE",
          "PROCESSING_MODIFY",
        ] as KisiStatus[]
      ).includes(club.kisiStatus)
    : false;

  const isXeroEnabled =
    !!dataXero?.status &&
    ["ENABLED", "ONBOARDING", "NEED_AUTHORIZATION"].includes(dataXero.status);

  return (
    <SettingsContainer
      title="Integrations and connected apps"
      subTitle="Enable superpowers by connecting Gymflow to partner apps and more."
    >
      <div className="flex flex-row flex-wrap gap-4">
        <IntegrationCard
          logo={
            <div className="bg-kisi-logo size-10 rounded-lg bg-contain bg-center bg-no-repeat" />
          }
          title="Kisi"
          description="Mobile check ins and access control solution. Offer 24 hour access via the Gymflow mobile app."
          switchComponent={
            isKisiProcessing ? (
              <Spinner />
            ) : (
              <Switch
                value={club?.kisiStatus === "ENABLED"}
                onChange={() => {
                  if (club?.kisiStatus === "ENABLED") {
                    toast.toast({
                      message:
                        "This operation is not supported yet. If you want to disable Kisi please contact support.",
                      intent: "warning",
                    });
                  }
                  if (club?.kisiStatus === "DISABLED") {
                    history.push(
                      createClubLink(
                        RouteLayout.Staff,
                        RouteFeature.KisiIntegration,
                        kisiIntegrationRoutes.Enable,
                      ),
                    );
                  }
                }}
              />
            )
          }
          footer={
            <>
              {club?.kisiStatus === "ENABLED" && (
                <LinkButton
                  className="text-secondary-500 hover:!text-secondary-500 flex flex-row gap-1 text-sm font-semibold hover:opacity-50"
                  to={createClubLink(
                    RouteLayout.Staff,
                    RouteFeature.KisiIntegration,
                    kisiIntegrationRoutes.Edit,
                  )}
                >
                  <Settings04Icon />
                  Update Integration
                </LinkButton>
              )}
              {club?.kisiStatus === "DISABLED" && (
                <LinkButton
                  className="text-secondary-500 hover:!text-secondary-500 flex flex-row gap-1 text-sm font-semibold hover:opacity-50"
                  to={createClubLink(
                    RouteLayout.Staff,
                    RouteFeature.KisiIntegration,
                    kisiIntegrationRoutes.Enable,
                  )}
                >
                  <Settings04Icon />
                  Enable Integration
                </LinkButton>
              )}
              {isKisiProcessing && "Processing..."}
            </>
          }
        />
        {featureFlags?.featureFlags.PORTAL_XERO && (
          <IntegrationCard
            logo={
              <div className="bg-xero-logo size-10 rounded-lg bg-contain bg-center bg-no-repeat" />
            }
            title="Xero"
            description="Automatically reconcile payouts from Gymflow in your Xero accounting system."
            switchComponent={
              isLoadingXero ? (
                <Spinner />
              ) : (
                <Switch
                  value={isXeroEnabled}
                  disabled={
                    club?.stripeAccountType !== "CONNECTED" ||
                    !ability.can(Verb.Edit, Subject.XeroConnect)
                  }
                  onChange={async () => {
                    if (!dataXero?.status) {
                      return;
                    }

                    try {
                      if (isXeroEnabled) {
                        await mutateAsyncDisableXero();
                      } else {
                        await mutateAsyncEnableXero();
                      }
                    } catch (e) {
                      if (isAxiosError(e)) {
                        toast.notifyDanger(e);
                      }
                    }
                  }}
                />
              )
            }
            footer={
              ability.can(Verb.Edit, Subject.XeroConnect) ? (
                <>
                  {club?.stripeAccountType !== "CONNECTED" && (
                    <CustomTooltip
                      asChild
                      message="Xero integration is unavailable for Standard / Not connected Stripe accounts. To update your account please contact support@gymflow.io"
                    >
                      <div className="flex flex-row gap-1">
                        <AlertCircleIcon
                          className="size-5"
                          pathClassName="stroke-gray-500"
                        />
                        <div className="text-sm font-semibold text-gray-400">
                          Unavailable For Your Account
                        </div>
                      </div>
                    </CustomTooltip>
                  )}
                  {isXeroEnabled &&
                    dataXero.status === "NEED_AUTHORIZATION" &&
                    club?.stripeAccountType === "CONNECTED" && (
                      <Button
                        className="!text-secondary-500 hover:!text-secondary-500 flex h-fit flex-row gap-1 p-0 text-sm font-semibold hover:opacity-50"
                        intent="transparent"
                        onClick={async () => {
                          const { data } = await refetchRedirectAuthUrlXero();

                          window.open(data?.authPageRedirectUrl, "_blank");
                        }}
                      >
                        <Settings04Icon />
                        Authorise Integration
                      </Button>
                    )}
                  {isXeroEnabled &&
                    ["ONBOARDING", "ENABLED"].includes(dataXero.status) &&
                    club?.stripeAccountType === "CONNECTED" && (
                      <Button
                        link={createClubLink(
                          RouteLayout.Staff,
                          RouteFeature.XeroIntegration,
                        )}
                        className="!text-secondary-500 hover:!text-secondary-500 flex h-fit flex-row gap-1 p-0 text-sm font-semibold hover:opacity-50"
                        intent="transparent"
                      >
                        <Settings04Icon />
                        {dataXero.status === "ENABLED"
                          ? "Update Integration"
                          : "Finish Setup"}
                      </Button>
                    )}
                </>
              ) : undefined
            }
          />
        )}
      </div>
    </SettingsContainer>
  );
}

const IntegrationCard = ({
  logo,
  title,
  description,
  switchComponent,
  footer,
}: {
  logo: ReactNode;
  title: string;
  description: string;
  switchComponent: ReactNode;
  footer?: ReactNode;
}) => {
  return (
    <div className="bg-gray-0 shadow-xs flex w-full flex-col rounded-2xl border border-gray-200 lg:w-[300px]">
      <div className="flex flex-col gap-3 p-6">
        <div className="flex items-center justify-between">
          <div className="flex gap-x-3">
            {logo}
            <div className="flex items-center text-lg font-semibold text-gray-950">
              {title}
            </div>
          </div>
          {switchComponent}
        </div>
        <div className="text-sm font-medium text-gray-950">{description}</div>
      </div>
      {footer && (
        <div className="flex flex-row items-center justify-center border-t border-gray-200 py-4">
          {footer}
        </div>
      )}
    </div>
  );
};
